/**
 * Small pieces of Messaging (slice 2e; Paper 60–65): the iMessage mark and its
 * tile, the `sms:` link's QR code, the waiting arc, Crew's contact card as it
 * arrives in Messages, and a thread's "via iMessage" and "Couldn't text this
 * reply." lines. Slice 2h (Paper 70, 77): the envelope mark, its copper tile,
 * "via email" and "Couldn't email this reply.".
 */
import { IMESSAGE_TINT, ImessageMark } from "@crew/ui/components/imessage-mark";
import { cn } from "@crew/ui/lib/utils";
import { CircleAlert } from "lucide-react";
import { useMemo } from "react";
import { encode } from "uqr";

/** The row's tile: the mark on iMessage's pale blue, as an iMessage thread's rail tile (S-324). */
export function ImessageTile({ muted = false }: { muted?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-md",
        muted && "opacity-50",
      )}
      style={{ background: IMESSAGE_TINT }}
    >
      <ImessageMark size={18} />
    </span>
  );
}

/** Email's mark (Paper 70, 77): an envelope in copper, or the hint grey under a message. */
function EmailMark({
  size = 14,
  tone = "copper",
  className,
}: {
  size?: number;
  tone?: "copper" | "hint" | "current";
  className?: string;
}) {
  const stroke =
    tone === "copper"
      ? "var(--color-copper)"
      : tone === "hint"
        ? "var(--color-hint)"
        : "currentColor";
  return (
    <svg
      aria-hidden
      data-email-mark
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={cn("shrink-0", className)}
    >
      <rect
        x="3.5"
        y="5.5"
        width="17"
        height="13"
        rx="2"
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
      />
      <path
        d="M4 7l8 6 8-6"
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The Email card's tile (Paper 70): the envelope on a pale copper; grey when unavailable. */
export function EmailTile({ muted = false }: { muted?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-md",
        muted ? "bg-muted" : "bg-[#F4ECE4]",
      )}
    >
      <EmailMark size={18} tone={muted ? "hint" : "copper"} />
    </span>
  );
}

/** A messaging thread's mark: iMessage's bubble, or email's envelope. */
export function MessagingMark({ app, size }: { app: string | null | undefined; size: number }) {
  if (app === "email") return <EmailMark size={size} />;
  return <ImessageMark size={size} />;
}

/** A QR code of `text` (the `sms:` link), drawn as one path, a module a unit. */
export function QrCode({ text, size = 130 }: { text: string; size?: number }) {
  const { path, n } = useMemo(() => {
    const qr = encode(text, { ecc: "M", border: 0 });
    let d = "";
    qr.data.forEach((row, y) =>
      row.forEach((on, x) => {
        if (on) d += `M${x} ${y}h1v1h-1z`;
      }),
    );
    return { path: d, n: qr.size };
  }, [text]);
  return (
    <svg
      role="img"
      aria-label="QR code: scan to text the code"
      data-qr={text}
      width={size}
      height={size}
      viewBox={`0 0 ${n} ${n}`}
      shapeRendering="crispEdges"
    >
      <path d={path} fill="var(--color-foreground)" />
    </svg>
  );
}

/** The copper arc that says a code is waiting (Paper 62). */
export function WaitingArc({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="shrink-0 animate-spin [animation-duration:1.4s]"
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke="rgb(31 42 68 / 14%)" strokeWidth="2.4" />
      <path
        d="M12 3a9 9 0 0 1 9 9"
        fill="none"
        stroke="var(--color-copper)"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Crew's contact card as Messages shows it (Paper 63): a picture, not a button. */
export function ContactCard({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("flex shrink-0 flex-col overflow-clip rounded-[18px] bg-[#E9E9EB]", className)}
    >
      <div className="flex items-center gap-2.5 px-3.5 py-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-foreground font-display text-[22px] leading-5.5 text-background">
          C
        </span>
        <span className="flex flex-col gap-px font-[system-ui,sans-serif]">
          <span className="text-body leading-[19px] font-semibold text-[#111111]">Crew</span>
          <span className="text-xs text-[#6E6E73]">Your Chief of Staff</span>
        </span>
      </div>
      <div className="border-t border-[#00000014] px-3.5 py-2.25 text-center font-[system-ui,sans-serif] text-sm leading-4.5 font-medium text-[#0B84FE]">
        Add to Contacts
      </div>
    </div>
  );
}

/** A message's `data.via` app, when it came by text (slice 2e spec §4 step 7) or email (2h). */
function viaOf(data: Record<string, unknown>): "imessage" | "email" | null {
  const via = data.via as { app?: unknown } | undefined;
  return via?.app === "imessage" || via?.app === "email" ? via.app : null;
}

/**
 * How a text found its chat (slice 2i spec §9), when it isn't simply the chat
 * it continued: it started this one, or swiped to reply to a text in it. It's
 * how a person sees a text that went to the wrong chat.
 */
function routeOf(data: Record<string, unknown>): string | null {
  const route = (data.via as { route?: { by?: unknown; chat?: unknown } } | undefined)?.route;
  if (route?.chat === "new") return "new chat";
  return route?.by === "swipe" ? "swipe-reply" : null;
}

/**
 * "via iMessage" or "via email", small under a message that came that way
 * (spec §6.3; Paper 64, 77), under its photos and files when it has any
 * (slice 2f spec §10); a text that started its chat, or swiped back to it,
 * says so: "via iMessage · new chat" (slice 2i spec §9).
 */
export function ViaNote({ data }: { data: Record<string, unknown> }) {
  const app = viaOf(data);
  if (!app) return null;
  const route = routeOf(data);
  return (
    <p data-via={app} className="mt-1 flex items-center gap-1.25 text-xs leading-4 text-hint">
      {app === "email" ? (
        <EmailMark size={12} tone="hint" />
      ) : (
        <ImessageMark size={12} tone="hint" />
      )}
      {app === "email" ? "via email" : "via iMessage"}
      {route ? <span data-route={route}>· {route}</span> : null}
    </p>
  );
}

/**
 * "Couldn't text this reply." (spec §5.3, the `messaging_failed` system line),
 * or "Couldn't email this reply." (slice 2h, `email_failed`), under the
 * reply, in line with its words (Paper 64).
 */
export function MessagingFailedLine({
  size,
  email = false,
}: {
  size: "md" | "sm";
  email?: boolean;
}) {
  return (
    <div
      data-author="system"
      data-event={email ? "email_failed" : "messaging_failed"}
      className={cn(
        "flex items-center gap-1.5 text-xs leading-4 text-muted-foreground",
        size === "sm" ? "pl-8.5" : "pl-10",
      )}
    >
      <CircleAlert aria-hidden className="size-3.25 shrink-0 text-destructive" />
      {email ? "Couldn't email this reply." : "Couldn't text this reply."} It's here in full.
    </div>
  );
}
